'use client'

import { cn } from '@brew/ui/cn'
import { ArrowUpRight, ChevronDown } from 'lucide-react'
import Image from 'next/image'
import { useId, useState, useSyncExternalStore } from 'react'
import { FOUNDERS_WIDGET } from './founders-widget-content'
import { SpeechBubbleTail } from './speech-bubble-tail'

/**
 * "Talk to the founders", bottom right (Paper "Founders card — v2
 * (illustration)" + the two launcher states). It starts collapsed to the
 * launcher at every width, so it never covers the page on landing; the
 * round launcher (or its "Book a demo" bubble) opens the card and
 * collapses it again. The whole card is one link to the demo booking page.
 * Opening scales the card up out of the launcher's corner; hovering the
 * card lifts the handset by cross-fading a second drawing over the first.
 * All state is per page view.
 */
/** A green dot with a soft ring radiating out of it. */
function OnlineDot({ className }: { className: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('relative flex shrink-0', className)}
    >
      <span className="absolute inset-0 animate-mk-radiate rounded-full bg-mk-success motion-reduce:hidden" />
      <span className="relative size-full rounded-full bg-mk-success" />
    </span>
  )
}

type Founder = keyof typeof FOUNDERS_WIDGET.photos

/** The server (and the first client render) always show this face. */
const SERVER_FACE: Founder = 'thomas'
let pickedFace: Founder | null = null
/** One face per page load, drawn the first time the browser asks. */
function readFace(): Founder {
  pickedFace ??= Math.random() < 0.5 ? 'philip' : 'thomas'
  return pickedFace
}
function subscribeNever() {
  return () => undefined
}

export function FoundersWidget() {
  const [isOpen, setIsOpen] = useState(false)
  // The card's drawings load once the visitor heads for the launcher (hover,
  // focus, or the open itself), never for a visitor who leaves it closed.
  const [isArtWanted, setIsArtWanted] = useState(false)
  const wantArt = () => setIsArtWanted(true)
  const open = (next: boolean) => {
    setIsArtWanted(true)
    setIsOpen(next)
  }
  // One face per page load, picked in the browser so the server and the
  // first client render agree.
  const face = useSyncExternalStore(subscribeNever, readFace, () => SERVER_FACE)
  const cardId = useId()

  return (
    <div className="fixed right-4 bottom-4 z-40 flex flex-col items-end gap-3 font-mk-body md:right-6 md:bottom-6">
      <a
        id={cardId}
        href={FOUNDERS_WIDGET.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${FOUNDERS_WIDGET.title}: ${FOUNDERS_WIDGET.cta}`}
        // `inert` while collapsed: not focusable and not read out, but the
        // element stays mounted so the close animation can play.
        inert={!isOpen}
        className={cn(
          'group/founders relative block w-[min(480px,calc(100vw-2rem))] rounded-2xl border border-mk-card-line bg-mk-card shadow-[0_12px_32px_#1e1e1e1f]',
          'origin-bottom-right transition-[opacity,scale,translate] motion-reduce:transition-none',
          'focus-visible:outline-2 focus-visible:outline-mk-accent focus-visible:outline-offset-2',
          isOpen
            ? 'translate-y-0 scale-100 opacity-100 duration-250 ease-[cubic-bezier(0.22,1,0.36,1)]'
            : 'pointer-events-none translate-y-2 scale-[0.92] opacity-0 duration-180 ease-in'
        )}
      >
        <div className="flex flex-col gap-5 p-6">
          <div className="flex flex-col gap-2 sm:w-[232px]">
            <span className="font-mk-display font-semibold text-[17px] text-mk-ink leading-6 tracking-[-0.015em]">
              {FOUNDERS_WIDGET.title}
            </span>
            <span className="text-[14px] text-mk-body leading-[21px] tracking-[-0.005em]">
              {FOUNDERS_WIDGET.body}
            </span>
          </div>
          {/* Styled as the button; the surrounding card is the link. */}
          <span className="flex h-9 w-max items-center gap-2 rounded-full bg-mk-ink px-3.5 font-medium text-[14px] text-mk-card leading-5 tracking-[-0.01em] transition-[filter] group-hover/founders:brightness-110">
            {FOUNDERS_WIDGET.cta}
            <ArrowUpRight aria-hidden="true" className="size-[13px]" />
          </span>
        </div>

        {/*
          Two aligned cut-out drawings in one box. The resting one stays put;
          the handset-up one fades in on top with an opaque card-coloured
          backing, so it covers the resting handset without the see-through
          dip a plain cross-fade of two cut-outs has. The backing is why the
          box stays inside the card. A 1px lift gives the still image a sense
          of weight shifting.
        */}
        {isArtWanted ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-0 bottom-0 hidden h-[160px] w-[232px] overflow-hidden rounded-br-2xl transition-[translate] duration-350 ease-out group-hover/founders:-translate-y-px motion-reduce:transition-none sm:block"
          >
            <Image
              src={FOUNDERS_WIDGET.imageRest}
              alt=""
              width={760}
              height={579}
              sizes="232px"
              // Asked for on intent (see `isArtWanted`): load it right away.
              loading="eager"
              className="absolute right-0 bottom-0 h-full w-full object-contain object-right-bottom"
            />
            <Image
              src={FOUNDERS_WIDGET.imageUp}
              alt=""
              width={760}
              height={579}
              sizes="232px"
              // Fetched with the card so the first hover fades instead of pops.
              loading="eager"
              className="absolute right-0 bottom-0 h-full w-full bg-mk-card object-contain object-right-bottom opacity-0 transition-opacity duration-220 ease-out group-hover/founders:opacity-100 group-hover/founders:duration-350 motion-reduce:transition-none"
            />
          </span>
        ) : null}
      </a>

      <div className="relative flex items-center gap-3">
        {/*
          Collapsed state: "Book a demo" as a speech bubble from the founders,
          with a tail toward the avatar. Always shown while collapsed; it
          opens the card like the avatar does, but the avatar is the one
          control assistive tech sees.
        */}
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => open(true)}
          onPointerEnter={wantArt}
          className={cn(
            // The shadow is a filter on the whole button, so the bubble and
            // its tail cast one shape.
            'relative flex h-9 cursor-pointer items-center whitespace-nowrap rounded-2xl bg-mk-card px-3.5 font-medium text-[13px] text-mk-ink leading-4 tracking-[-0.005em] [filter:drop-shadow(0_4px_10px_#1e1e1e1f)_drop-shadow(0_0_0.5px_#1e1e1e33)]',
            'transition-[opacity,translate] duration-200 ease-out motion-reduce:transition-none',
            isOpen
              ? 'pointer-events-none translate-x-1 opacity-0'
              : 'translate-x-0 opacity-100 delay-150'
          )}
        >
          {FOUNDERS_WIDGET.bubble}
          <SpeechBubbleTail className="absolute right-[-12px] bottom-[1px] text-mk-card" />
        </button>
        <button
          type="button"
          onClick={() => open(!isOpen)}
          onPointerEnter={wantArt}
          onFocus={wantArt}
          aria-expanded={isOpen}
          aria-controls={cardId}
          aria-label={
            isOpen ? 'Hide the founders card' : FOUNDERS_WIDGET.bubble
          }
          className={cn(
            'relative size-14 shrink-0 cursor-pointer overflow-visible rounded-full shadow-[0_8px_24px_#1e1e1e29] transition-[background-color,scale] duration-200 ease-out hover:scale-105 focus-visible:outline-2 focus-visible:outline-mk-accent focus-visible:outline-offset-2 motion-reduce:transition-none',
            isOpen ? 'bg-mk-ink' : 'border border-mk-card-line bg-mk-card'
          )}
        >
          {/* One founder's photo, picked per page view. */}
          <span
            aria-hidden="true"
            className={cn(
              'absolute inset-0 overflow-hidden rounded-full transition-opacity duration-200 motion-reduce:transition-none',
              isOpen ? 'opacity-0' : 'opacity-100'
            )}
          >
            <Image
              src={FOUNDERS_WIDGET.photos[face]}
              alt=""
              fill
              sizes="112px"
              // Ready before the card is ever collapsed.
              loading="eager"
              className="object-cover object-[50%_30%]"
            />
          </span>
          <ChevronDown
            aria-hidden="true"
            strokeWidth={2.4}
            className={cn(
              'absolute top-1/2 left-1/2 size-[22px] -translate-x-1/2 -translate-y-1/2 text-mk-card transition-[opacity,rotate] duration-200 motion-reduce:transition-none',
              isOpen ? 'rotate-0 opacity-100' : '-rotate-90 opacity-0'
            )}
          />
          {/* Online dot, only while collapsed: the card carries its own. */}
          <span
            aria-hidden="true"
            className={cn(
              'absolute -top-px -right-px flex size-4 items-center justify-center rounded-full bg-mk-card transition-opacity duration-200 motion-reduce:transition-none',
              isOpen ? 'opacity-0' : 'opacity-100'
            )}
          >
            <OnlineDot className="size-2.5" />
          </span>
        </button>
      </div>
    </div>
  )
}
